import React, { useState } from 'react';
import { Tag, Input, Button, Popconfirm, message, Tooltip, Select } from 'antd';
import type { TableColumnsType } from 'antd';
import { CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { addKeyData, formatDate } from 'utils';
import type { SearchProps } from 'antd/es/input/Search';
import Api from 'configs';

import { TableCustom } from 'components/element/TableCustom';
import { PaginationCustom } from 'components/element/PaginationCustom';
import { useGetShopClosedReports } from 'hooks/report';

export const ShopClosedReportsTab: React.FC = () => {
  const [filters, setFilters] = useState<any>({
    page: 1,
    pageSize: 10,
    search: '',
    status: 'all',
  });
  const { data: getData, isLoading, refetch } = useGetShopClosedReports(filters);

  const handleReopenShop = async (shopId: string, shopName: string) => {
    try {
      await Api.post(`/shops/${shopId}/reopen`);
      message.success(`Đã mở lại quán "${shopName}" thành công!`);
      refetch();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Có lỗi xảy ra khi mở lại quán');
    }
  };

  const columns: TableColumnsType = [
    {
      title: 'Mã đơn hàng',
      dataIndex: 'order',
      key: 'code',
      align: 'center',
      width: 140,
      render: (order: any) => {
        return (
          <span className="font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">
            {order?.code || '—'}
          </span>
        );
      },
    },
    {
      title: 'Quán ăn',
      dataIndex: 'shop',
      key: 'shop',
      align: 'left',
      render: (shop: any) => {
        return (
          <div className="flex flex-col">
            <span className="font-bold text-gray-900 text-sm">{shop?.name || 'Quán ăn'}</span>
            {shop?.numberPhone && (
              <span className="text-xs text-blue-600 font-medium">📞 {shop.numberPhone}</span>
            )}
            {shop?.address && (
              <span className="text-xs text-gray-500 line-clamp-1" title={shop.address}>
                📍 {shop.address}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tài xế (Shipper) báo',
      key: 'driver',
      align: 'left',
      render: (_: any, record: any) => {
        const driverName = record?.driverUser?.name || record?.driverShip?.name || 'Tài xế';
        const driverPhone = record?.driverUser?.numberPhone || record?.driverShip?.phone;
        const bks = record?.driverShip?.vehicleBKS;
        return (
          <div className="flex flex-col">
            <span className="font-semibold text-gray-800 text-sm">{driverName}</span>
            {driverPhone && (
              <span className="text-xs text-gray-600">📱 {driverPhone}</span>
            )}
            {bks && (
              <span className="text-xs text-amber-700 font-medium">🛵 BKS: {bks}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Lý do báo đóng cửa',
      dataIndex: 'reason',
      key: 'reason',
      align: 'center',
      render: (reason: string) => {
        return (
          <span className="text-xs text-red-700 bg-red-50 px-2.5 py-1.5 rounded border border-red-200 inline-block font-medium max-w-xs">
            ⚠️ {reason || 'Tài xế báo quán đóng cửa'}
          </span>
        );
      },
    },
    {
      title: 'Thời gian báo',
      dataIndex: 'closedAt',
      key: 'closedAt',
      align: 'center',
      width: 160,
      render: (data: any) => {
        return <div className="text-xs text-gray-700 font-medium">{formatDate(data)}</div>;
      },
    },
    {
      title: 'Tình trạng quán',
      key: 'status',
      align: 'center',
      width: 140,
      render: (_: any, record: any) => {
        if (record?.isReopened) {
          return (
            <Tooltip title={record?.reopenedAt ? `Mở lại lúc: ${formatDate(record.reopenedAt)}` : undefined}>
              <Tag color="success" className="font-medium">
                Đã mở lại
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tag color="error" className="font-medium">
            Đang tạm đóng
          </Tag>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      align: 'center',
      width: 130,
      render: (_: any, record: any) => {
        const shopId = record?.shop?._id;
        const shopName = record?.shop?.name || 'quán';
        const isReopened = record?.isReopened;

        if (isReopened) {
          return <span className="text-xs text-gray-400 italic">Đã xử lý</span>;
        }

        return (
          <Popconfirm
            title={`Mở lại quán "${shopName}"`}
            description="Bạn có chắc chắn muốn mở lại quán này ngay bây giờ?"
            okText="Mở lại quán"
            cancelText="Hủy"
            onConfirm={() => shopId && handleReopenShop(shopId, shopName)}>
            <Button
              size="small"
              type="primary"
              icon={<CheckCircleOutlined />}
              className="bg-green-600 hover:bg-green-500 text-xs">
              Mở lại quán
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const onSearchValue: SearchProps['onSearch'] = (value: string) => {
    setFilters({ ...filters, page: 1, search: value });
  };

  return (
    <div className="flex-col gap-4 mt-2">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-sm">
            Tổng số: <b>{getData?.total || 0}</b> lượt shipper báo quán đóng cửa
          </span>
          <Select
            value={filters.status}
            style={{ width: 150 }}
            onChange={(val) => setFilters({ ...filters, page: 1, status: val })}
            options={[
              { value: 'all', label: 'Tất cả trạng thái' },
              { value: 'closed', label: 'Đang tạm đóng' },
              { value: 'reopened', label: 'Đã mở lại' },
            ]}
          />
        </div>
        <div className="flex items-center gap-2">
          <Input.Search
            className="w-80"
            placeholder="Tìm theo tên quán, shipper, mã đơn..."
            onSearch={onSearchValue}
            enterButton
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} title="Tải lại" />
        </div>
      </div>

      <div className="flex flex-col items-end gap-3">
        <TableCustom
          isLoading={isLoading}
          columns={columns}
          data={addKeyData(getData?.result)}
        />
        <PaginationCustom
          totalPage={getData?.totalPage ?? 1}
          current={getData?.page ?? 1}
          pageSize={getData?.pageSize ?? 10}
          onChange={(page, pageSize) => {
            setFilters({ ...filters, page, pageSize });
          }}
          onShowSizeChange={(current, size) => {
            setFilters({ ...filters, page: current, pageSize: size });
          }}
        />
      </div>
    </div>
  );
};
